import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

const UFS = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];

const EMPTY_FORM = {
  name: '', phone: '', email: '', document: '',
  address: { cep: '', street: '', number: '', complement: '', district: '', city: '', state: '' },
};

const onlyDigits = v => String(v || '').replace(/\D/g, '');

const formatCep = v => {
  const d = onlyDigits(v).slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
};

export default function Suppliers() {
  const [suppliers, setSuppliers]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(false);
  const [editing, setEditing]       = useState(null);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [saving, setSaving]         = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [success, setSuccess]       = useState('');
  const [error, setError]           = useState('');

  const fetchSuppliers = useCallback(async () => {
    try {
      const res = await api.get('/admin/suppliers');
      setSuppliers(res.data);
    } catch {
      setError('Erro ao carregar fornecedores.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setSuccess('');
    setError('');
    setShowForm(true);
  };

  const openEdit = (supplier) => {
    setEditing(supplier);
    setForm({
      name:     supplier.name     || '',
      phone:    supplier.phone    || '',
      email:    supplier.email    || '',
      document: supplier.document || '',
      address: {
        cep:        supplier.address?.cep        || '',
        street:     supplier.address?.street     || '',
        number:     supplier.address?.number     || '',
        complement: supplier.address?.complement || '',
        district:   supplier.address?.district   || '',
        city:       supplier.address?.city       || '',
        state:      supplier.address?.state      || '',
      },
    });
    setSuccess('');
    setError('');
    setShowForm(true);
  };

  const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const setAddr  = (field, value) => setForm(prev => ({ ...prev, address: { ...prev.address, [field]: value } }));

  const handleCepBlur = async () => {
    const cep = onlyDigits(form.address.cep);
    if (cep.length !== 8) return;
    setCepLoading(true);
    try {
      const r    = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await r.json();
      if (!data.erro) {
        setForm(prev => ({
          ...prev,
          address: {
            ...prev.address,
            street:   data.logradouro || prev.address.street,
            district: data.bairro     || prev.address.district,
            city:     data.localidade || prev.address.city,
            state:    data.uf         || prev.address.state,
          },
        }));
      }
    } catch { /* silencioso */ }
    finally { setCepLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSuccess('');
    setError('');
    try {
      const payload = { ...form, address: { ...form.address, cep: onlyDigits(form.address.cep) } };
      if (editing) {
        const res = await api.put(`/admin/suppliers/${editing._id}`, payload);
        setSuppliers(prev => prev.map(s => s._id === editing._id ? res.data : s));
        setSuccess('Fornecedor atualizado com sucesso!');
      } else {
        const res = await api.post('/admin/suppliers', payload);
        setSuppliers(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
        setSuccess('Fornecedor cadastrado com sucesso!');
        setForm(EMPTY_FORM);
        setEditing(null);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao salvar fornecedor.');
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setShowForm(false);
    setEditing(null);
    setForm(EMPTY_FORM);
    setSuccess('');
    setError('');
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h1 className="page-title" style={{ margin: 0 }}>Fornecedores</h1>
        {!showForm && (
          <button className="btn-admin-primary" onClick={openNew}>+ Novo Fornecedor</button>
        )}
      </div>

      {/* ── Formulário ── */}
      {showForm && (
        <div className="admin-form-card" style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
            {editing ? 'Editar Fornecedor' : 'Novo Fornecedor'}
          </h2>

          {success && <div className="alert-success">{success}</div>}
          {error   && <div className="alert-error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-grid-2">
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Nome do Fornecedor *</label>
                <input className="form-control-admin" value={form.name}
                  onChange={e => setField('name', e.target.value)} required disabled={saving} />
              </div>
              <div className="form-group">
                <label>Telefone</label>
                <input className="form-control-admin" value={form.phone}
                  onChange={e => setField('phone', e.target.value)} disabled={saving} />
              </div>
              <div className="form-group">
                <label>E-mail</label>
                <input className="form-control-admin" type="email" value={form.email}
                  onChange={e => setField('email', e.target.value)} disabled={saving} />
              </div>
              <div className="form-group">
                <label>CNPJ / CPF</label>
                <input className="form-control-admin" value={form.document}
                  onChange={e => setField('document', e.target.value)} disabled={saving} />
              </div>
            </div>

            <fieldset style={{ border: '1px solid #ddd', borderRadius: 6, padding: '12px 16px', marginTop: 8 }}>
              <legend style={{ fontSize: 13, fontWeight: 600, color: '#555', padding: '0 6px' }}>
                Endereço (remetente no cálculo de frete)
              </legend>
              <div className="form-grid-2">
                <div className="form-group">
                  <label>CEP * {cepLoading && <small style={{ color: '#888' }}>(buscando...)</small>}</label>
                  <input className="form-control-admin" value={form.address.cep}
                    onChange={e => setAddr('cep', formatCep(e.target.value))}
                    onBlur={handleCepBlur} placeholder="00000-000" disabled={saving} />
                </div>
                <div className="form-group">
                  <label>Estado</label>
                  <select className="form-control-admin" value={form.address.state}
                    onChange={e => setAddr('state', e.target.value)} disabled={saving}>
                    <option value="">Selecione</option>
                    {UFS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Logradouro</label>
                  <input className="form-control-admin" value={form.address.street}
                    onChange={e => setAddr('street', e.target.value)} disabled={saving} />
                </div>
                <div className="form-group">
                  <label>Número</label>
                  <input className="form-control-admin" value={form.address.number}
                    onChange={e => setAddr('number', e.target.value)} disabled={saving} />
                </div>
                <div className="form-group">
                  <label>Complemento</label>
                  <input className="form-control-admin" value={form.address.complement}
                    onChange={e => setAddr('complement', e.target.value)} disabled={saving} />
                </div>
                <div className="form-group">
                  <label>Bairro</label>
                  <input className="form-control-admin" value={form.address.district}
                    onChange={e => setAddr('district', e.target.value)} disabled={saving} />
                </div>
                <div className="form-group">
                  <label>Cidade</label>
                  <input className="form-control-admin" value={form.address.city}
                    onChange={e => setAddr('city', e.target.value)} disabled={saving} />
                </div>
              </div>
            </fieldset>

            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button type="submit" className="btn-admin-primary" disabled={saving}>
                {saving ? 'Salvando...' : (editing ? 'Atualizar' : 'Cadastrar')}
              </button>
              <button type="button" className="btn-admin-secondary" onClick={cancel} disabled={saving}>
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Lista ── */}
      {loading && <p style={{ color: '#999' }}>Carregando fornecedores...</p>}

      {!loading && suppliers.length === 0 && (
        <div className="empty-state">Nenhum fornecedor cadastrado.</div>
      )}

      {!loading && suppliers.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                <th style={th}>Nome</th>
                <th style={th}>Telefone</th>
                <th style={th}>E-mail</th>
                <th style={th}>Cidade / UF</th>
                <th style={th}>CEP</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {suppliers.map(s => (
                <tr key={s._id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={td}><strong>{s.name}</strong></td>
                  <td style={td}>{s.phone || '—'}</td>
                  <td style={td}>{s.email || '—'}</td>
                  <td style={td}>{s.address?.city ? `${s.address.city} / ${s.address.state}` : '—'}</td>
                  <td style={td}>{s.address?.cep || '—'}</td>
                  <td style={td}>
                    <button className="btn-admin-sm-yellow" onClick={() => openEdit(s)}>✏️ Editar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

const th = { padding: '8px 12px', fontWeight: 600, fontSize: 13, color: '#555', borderBottom: '2px solid #ddd' };
const td = { padding: '10px 12px', verticalAlign: 'middle' };
